<template>
  <b-card class="individually-trading-card">
    <!-- Cabeçalho do cartão com nome -->
    <div class="card-header d-flex justify-content-between align-items-center flex-wrap">
      <div class="card-title">
        <h4 class="mb-0">{{ formattedName.namePart1 }}</h4>
        <small class="text-muted">{{ formattedName.namePart2 }}</small>
      </div>
    </div>

    <!-- Visão geral dos resultados -->
    <my-results-goal-overview :data="data.goalOverview" :noHeader="true" />

    <!-- Botões de ação -->
    <div class="action-buttons mt-3">
      <b-button block variant="warning" size="sm" class="mb-2" @click="allocateValue(item._id)" v-if="canAllocateMore">
        <b-icon icon="plus-circle" class="mr-1"></b-icon>
        {{ $t("Adicionar fundos") }}
      </b-button>

      <b-row class="no-gutters">
        <b-col cols="12" md="6" class="pr-md-1 mb-2">
          <b-button block variant="outline-success" @click="bonusReinvest(item._id)" :disabled="true">
            <div class="d-flex justify-content-between align-items-center">
              <span>{{ $t("Reinvestir bônus") }}</span>
              <b-badge variant="success">{{ moneyFormat(availableBonus) }}</b-badge>
            </div>
          </b-button>
        </b-col>
        <b-col cols="12" md="6" class="pl-md-1 mb-2">
          <b-button block variant="outline-info" @click="incomeReinvest(item._id)">
            <div class="d-flex justify-content-between align-items-center">
              <span>{{ $t("Reinvestir lucro") }}</span>
              <b-badge variant="info">{{ moneyFormat(availableIncome) }}</b-badge>
            </div>
          </b-button>
        </b-col>
      </b-row>
    </div>

    <!-- Informações adicionais -->
    <b-list-group flush class="mt-3">
      <b-list-group-item v-for="(robotInfo, index) in data.goalOverview.robotInfo" :key="index">
        <div class="d-flex justify-content-between">
          <span>{{ robotInfo.title }}</span>
          <span class="font-weight-bold text-right">{{ robotInfo.value }}</span>
        </div>
      </b-list-group-item>
    </b-list-group>

    <!-- Botões de saque -->
    <div class="withdraw-buttons text-center mt-3">
      <b-button block variant="success" size="sm" class="mb-2" @click="() => $emit('withdraw', { item, type: 'bonus' })"
        :disabled="availableBonus < 20">
        <b-icon icon="cash-stack" class="mr-1"></b-icon>
        {{ $t("Retirar bônus") }} | {{ moneyFormat(availableBonus) }}
      </b-button>
      <b-button block variant="primary" size="sm" class="mb-2"
        @click="() => $emit('withdraw', { item, type: 'income' })"
        :disabled="availableIncome <= 0 || isNotWithdrawalDay">
        <b-icon icon="bank" class="mr-1"></b-icon>
        {{ $t("Retirar lucro") }} | {{ moneyFormat(availableIncome) }}
      </b-button>
      <small class="text-primary">
        <b>{{ $t("Saques de lucro apenas nos dias 5 e 20") }}</b>
      </small>
    </div>

    <!-- Modal para adicionar fundos -->
    <b-modal :id="'modal-allocateValue-' + item._id" centered :title="$t('Adicionar Fundos')" hide-footer size="lg"
      no-close-on-backdrop>
      <div v-if="loadingPayment" class="text-center">
        {{ $t("Aguarde! Carregando...") }}
      </div>
      <div v-else>
        <div v-if="paymentDetails && paymentDetails.searchPendingPay">
          <!-- Exibe detalhes do pagamento pendente -->
          <div class="text-center mb-1">
            <h5>{{ $t("Tempo restante para pagamento:") }} <span class="text-danger">{{ countdown }}</span>
            </h5>
          </div>

          <div class="text-center">
            <h5>{{ $t("Valor a ser transferido:") }} <span class="text-primary">{{
              paymentDetails.searchPendingPay.value }} USDT</span></h5>
          </div>

          <div class="text-center my-2">
            <h5>{{ $t("Rede:") }} <span class="badge badge-warning">Binance Smart Chain (BSC)</span></h5>
          </div>

          <div class="text-center my-1">
            <h5 class="mb-0">{{ $t("Endereço de depósito:") }}</h5>
            <div class="d-flex justify-content-center align-items-center">
              <span :class="{ 'text-success': addressCopied, 'text-primary': !addressCopied }">
                {{ addressCopied ? $t("Endereço copiado") : compactAddress(paymentDetails.depositAddress) }}
              </span>
              <b-button variant="link" @click="copyToClipboard(paymentDetails.depositAddress)" class="ml-1">
                <i class="fas fa-copy"></i> {{ $t('Copiar') }}
              </b-button>
            </div>
          </div>


          <!-- Exibe o QR Code -->
          <div v-if="qrCodeData" class="text-center my-1">
            <h5>{{ $t("Escaneie o QR Code para abrir o endereço de depósito na sua carteira") }}:</h5>
            <img :src="qrCodeData" alt="QR Code" class="qr-code" />
          </div>

          <!-- Mensagem de Observação -->
          <div class="text-center mt-3">
            <p class="text-muted">{{ $t("Se você já fez o pagamento, aguarde a validação do sistema.") }}
            </p>
          </div>
        </div>
        <div v-else>
          <div class="mb-2">{{ $t("Quanto você quer adicionar?") }}</div>
          <b-input-group>
            <b-input-group-prepend is-text>$</b-input-group-prepend>
            <b-form-input v-model="allocationValue" name="allocation-value" placeholder="0.00" type="number" />
          </b-input-group>
          <b-alert variant="warning" :show="!hideMsg" class="mt-2" v-if="remainingAllocation > 0">
            <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
            <span>
              {{
                $t("Você pode adicionar até:") +
                " " +
                moneyFormat(remainingAllocation)
              }}
            </span>
          </b-alert>
          <b-button variant="primary" block class="mt-2" @click="allocateValueInTrading(item._id)"
            :disabled="btnLoading">
            {{ btnLoading ? $t("Aguarde...") : $t("Gerar endereço para depósito") }}
          </b-button>
          <alert :text="textAlert" :color="colorAlert" :showAlert="showAlert" class="mt-2"
            @changeVariableAlert="(val) => (showAlert = val)" />
        </div>
      </div>
    </b-modal>

    <!-- Modal para reinvestir bônus -->
    <b-modal :id="'modal-bonusReinvest-' + item._id" centered :title="$t('Reinvestir Bônus')" hide-footer size="lg"
      no-close-on-backdrop>
      <div>
        <div class="mb-2">
          {{ $t("Bônus disponível:") }}
          <span class="text-warning">{{ moneyFormat(availableBonus) }}</span>
        </div>
        <div>{{ $t("Quanto deseja reinvestir?") }}</div>
        <b-input-group class="mt-1">
          <b-input-group-prepend is-text>$</b-input-group-prepend>
          <b-form-input v-model="allocationBonusValue" name="allocation-bonus-value" placeholder="0.00" type="number" />
        </b-input-group>
        <b-alert variant="warning" :show="!hideMsg" class="mt-2" v-if="remainingAllocation > 0">
          <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
          <span>
            {{
              $t("Limite para adicionar:") +
              " " +
              moneyFormat(remainingAllocation)
            }}
          </span>
        </b-alert>
        <b-button variant="primary" block class="mt-2" :disabled="btnLoading"
          @click="() => relocate(item._id, 'bonus', 'contribution')">
          {{ btnLoading ? $t("Aguarde...") : $t("Confirmar") }}
        </b-button>
        <b-button variant="warning" block class="mt-2" :disabled="btnLoading"
          @click="() => (otherLicense = !otherLicense)">
          {{ $t("Reinvestir em outro plano") }}
        </b-button>
        <div class="p-2" v-if="otherLicense">
          <small>
            {{ $t("Selecione o plano para reinvestir o bônus:") }}
          </small>
          <b-form-radio-group v-model="licenseId" name="license-options" class="mt-1">
            <b-form-radio v-for="license in userLicenses" :key="license._id" :value="license._id" class="mr-1">
              {{ license._id }} | {{ moneyFormat(license.licenseValue) }}
            </b-form-radio>
          </b-form-radio-group>
          <div class="d-flex justify-content-center mt-2">
            <b-button variant="warning" :disabled="!licenseId || btnLoading"
              @click="() => relocate(item._id, 'bonus', 'otherContribution')">
              {{ btnLoading ? $t("Aguarde...") : $t("Confirmar") }}
            </b-button>
          </div>
        </div>
        <b-button variant="success" block class="mt-2" :disabled="btnLoading"
          @click="() => relocate(item._id, 'bonus', 'license')">
          {{ btnLoading ? $t("Aguarde...") : $t("Comprar novo plano") }}
        </b-button>
        <alert :text="textAlert" :color="colorAlert" :showAlert="showAlert" class="mt-2"
          @changeVariableAlert="(val) => (showAlert = val)" />
      </div>
    </b-modal>

    <!-- Modal para reinvestir lucro -->
    <b-modal :id="'modal-incomeReinvest-' + item._id" centered :title="$t('Reinvestir Lucro')" hide-footer size="lg"
      no-close-on-backdrop>
      <div>
        <div class="mb-2">
          {{ $t("Lucro disponível:") }}
          <span class="text-warning">{{ moneyFormat(availableIncome) }}</span>
        </div>
        <div>{{ $t("Quanto deseja reinvestir?") }}</div>
        <b-input-group class="mt-1">
          <b-input-group-prepend is-text>$</b-input-group-prepend>
          <b-form-input v-model="allocationIncomeValue" name="allocation-income-value" placeholder="0.00"
            type="number" />
        </b-input-group>
        <b-alert variant="warning" :show="!hideMsg" class="mt-2" v-if="remainingAllocation > 0">
          <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
          <span>
            {{
              $t("Limite para adicionar:") +
              " " +
              moneyFormat(remainingAllocation)
            }}
          </span>
        </b-alert>
        <b-button variant="primary" block class="mt-2" :disabled="btnLoading"
          @click="() => relocate(item._id, 'income', 'contribution')">
          {{ btnLoading ? $t("Aguarde...") : $t("Confirmar") }}
        </b-button>
        <b-button variant="warning" block class="mt-2" :disabled="btnLoading"
          @click="() => (otherLicense = !otherLicense)">
          {{ $t("Reinvestir em outro plano") }}
        </b-button>
        <div class="p-2" v-if="otherLicense">
          <small>
            {{ $t("Selecione o plano para reinvestir o lucro:") }}
          </small>
          <b-form-radio-group v-model="licenseId" name="license-options-income" class="mt-1">
            <b-form-radio v-for="license in userLicenses" :key="license._id" :value="license._id" class="mr-1">
              {{ license._id }} | {{ moneyFormat(license.licenseValue) }}
            </b-form-radio>
          </b-form-radio-group>
          <div class="d-flex justify-content-center mt-2">
            <b-button variant="warning" :disabled="!licenseId || btnLoading"
              @click="() => relocate(item._id, 'income', 'otherContribution')">
              {{ btnLoading ? $t("Aguarde...") : $t("Confirmar") }}
            </b-button>
          </div>
        </div>
        <b-button variant="success" block class="mt-2" :disabled="btnLoading"
          @click="() => relocate(item._id, 'income', 'license')">
          {{ btnLoading ? $t("Aguarde...") : $t("Comprar novo plano") }}
        </b-button>
        <alert :text="textAlert" :color="colorAlert" :showAlert="showAlert" class="mt-2"
          @changeVariableAlert="(val) => (showAlert = val)" />
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import MyResultsGoalOverview from "./MyResultsGoalOverview.vue";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

import {
  BCard,
  BButton,
  BModal,
  BAlert,
  BFormRadioGroup,
  BFormRadio,
  BFormInput,
  BListGroup,
  BListGroupItem,
  BBadge,
  BIcon,
  BInputGroup,
  BInputGroupPrepend,
  BRow,
  BCol,
} from "bootstrap-vue";
import { mapGetters } from "vuex";
import QRCode from "qrcode";

export default {
  components: {
    MyResultsGoalOverview,
    BCard,
    BButton,
    BModal,
    BAlert,
    BFormRadioGroup,
    BFormRadio,
    BFormInput,
    BListGroup,
    BListGroupItem,
    BBadge,
    BIcon,
    BInputGroup,
    BInputGroupPrepend,
    BRow,
    BCol,
  },
  props: {
    item: Object,
  },
  data() {
    return {
      loadingPayment: false,
      paymentDetails: {
        searchPendingPay: {
          value: 0,
          crypto: '',
          createdAt: ''
        },
        depositAddress: ''
      },
      addressCopied: false,
      countdown: "00:00:00",
      qrCodeData: null,
      btnLoading: false,
      allocationValue: null,
      allocationBonusValue: null,
      allocationIncomeValue: null,
      userLicenses: [],
      otherLicense: false,
      licenseId: null,
      hideMsg: false,
      showAlert: false,
      colorAlert: "",
      textAlert: "",
      data: {
        goalOverview: {
          initialValue: this.item.initialValue,
          licenseValue: this.item.licenseValue,
          totalProfit: this.item.totalProfit,
          bonus: this.item.bonus,
          currentValue: this.item.totalProfit + this.item.bonus,
          robotInfo: [
            {
              title: this.$t("Data de aquisição"),
              value: this.formatDate(new Date(this.item.startDate)),
            },
            {
              title: this.$t("Valor do plano"),
              value: this.moneyFormat(this.item.licenseValue),
            },
            {
              title: this.$t("Data do aporte"),
              value: this.item.initialValueDate
                ? this.formatDate(new Date(this.item.initialValueDate))
                : "--",
            },
            {
              title: this.$t("Total aportado"),
              value: this.item.initialValue
                ? this.moneyFormat(this.item.initialValue)
                : this.moneyFormat(0),
            },
            {
              title: this.$t("Bônus recebidos"),
              value: this.moneyFormat(this.item.bonus),
            },
            {
              title: this.$t("Disponível para saque"),
              value: this.moneyFormat(
                this.item.totalProfit +
                this.item.bonus -
                this.item.totalWhitdraw -
                this.item.totalIncomeWithdrawal
              ),
            },
            {
              title: this.$t("Bônus sacados"),
              value: this.moneyFormat(this.item.totalWhitdraw),
            },
            {
              title: this.$t("Lucro sacado"),
              value: this.moneyFormat(this.item.totalIncomeWithdrawal),
            },
          ],
        },
      },
    };
  },
  computed: {
    ...mapGetters(["returnUser"]),
    formattedName() {
      const [namePart1, namePart2] = this.item.name.split("-");
      return { namePart1, namePart2 };
    },
    availableBonus() {
      return this.item.bonus - this.item.totalWhitdraw;
    },
    availableIncome() {
      return this.item.totalProfit - this.item.totalIncomeWithdrawal;
    },
    totalInvested() {
      return this.item.initialValue + this.item.licenseValue;
    },
    totalReturn() {
      return this.item.bonus + this.item.totalProfit;
    },
    canAllocateMore() {
      return this.remainingAllocation > 0;
    },
    remainingAllocation() {

      let limitInvestment = 0;

      if (this.item.licenseValue === 250) {
        limitInvestment = 2500
      }
      if (this.item.licenseValue === 500) {
        limitInvestment = 10000
      }
      if (this.item.licenseValue === 1250) {
        limitInvestment = 25000
      }
      if (this.item.licenseValue === 2500) {
        limitInvestment = 75000
      }

      return limitInvestment - this.item.initialValue;
    },
    isNotWithdrawalDay() {
      const date = new Date();
      const day = date.getDate();
      return day !== 5 && day !== 20;
    },
  },
  methods: {
    compactAddress(address) {
      // Exibir os primeiros 6 e os últimos 4 caracteres do endereço
      return address ? `${address.slice(0, 10)}...${address.slice(-10)}` : '';
    },
    copyToClipboard(address) {
      navigator.clipboard.writeText(address).then(() => {
        this.addressCopied = true;
        setTimeout(() => {
          this.addressCopied = false;
        }, 3000); // Após 3 segundos, retorna o endereço
        this.$bvToast.toast(this.$t('Endereço copiado!'), {
          title: this.$t('Sucesso'),
          variant: 'success',
          solid: true,
        });
      }).catch(err => {
        // console.error('Erro ao copiar endereço:', err);
      });
    },
    formatDate(date) {
      return format(date, "dd/MM/yyyy HH:mm", { locale: ptBR });
    },
    differenceDate(date) {
      const dataFornecida = new Date(date);
      const dataAtual = new Date();

      const diferencaEmMilissegundos = dataAtual - dataFornecida;

      const umDiaEmMilissegundos = 1000 * 60 * 60 * 24;
      const umHoraEmMilissegundos = 1000 * 60 * 60;
      const umMinutoEmMilissegundos = 1000 * 60;

      const dias = Math.floor(
        diferencaEmMilissegundos / umDiaEmMilissegundos
      );
      const horas = Math.floor(
        (diferencaEmMilissegundos % umDiaEmMilissegundos) /
        umHoraEmMilissegundos
      );
      const minutos = Math.floor(
        (diferencaEmMilissegundos % umHoraEmMilissegundos) /
        umMinutoEmMilissegundos
      );

      const horasComZero = horas < 10 ? `0${horas}` : `${horas}`;
      const minutosComZero = minutos < 10 ? `0${minutos}` : `${minutos}`;

      return `${dias} dias, ${horasComZero}h:${minutosComZero}m`;
    },
    isDatePlus15DaysGreater(datePassed) {
      const initialDate = new Date(datePassed);
      initialDate.setDate(initialDate.getDate() + 15);
      const currentDate = new Date();
      return initialDate > currentDate;
    },
    allocateValue(id) {
      this.$bvModal.show(`modal-allocateValue-${id}`);
      this.getDepositIntent(id);
    },
    bonusReinvest(id) {
      this.$bvModal.show(`modal-bonusReinvest-${id}`);
    },
    incomeReinvest(id) {
      this.$bvModal.show(`modal-incomeReinvest-${id}`);
    },
    relocate(id, relocateType, to) {
      this.btnLoading = true;
      const data = {
        robotId: id,
        value:
          relocateType === "bonus"
            ? this.allocationBonusValue
            : this.allocationIncomeValue,
        relocate: relocateType,
        to: to,
      };
      if (to === "otherContribution") {
        data.licenseId = this.licenseId;
      }
      this.$store
        .dispatch("relocateValue", data)
        .then(() => {
          this.showAlert = true;
          this.colorAlert = "success";
          this.textAlert = this.$t("Valor reinvestido com sucesso");
          this.$emit("reloadRobots");
          setTimeout(() => {
            this.$bvModal.hide(`modal-allocateValue-${id}`);
            this.btnLoading = false;
          }, 5000);
        })
        .catch((error) => {
          this.btnLoading = false;
          this.showAlert = true;
          this.colorAlert = "danger";
          this.textAlert = error.message;
        });
    },
    getDepositIntent(id) {
      this.loadingPayment = true;
      const data = {
        planId: id
      };
      this.$store
        .dispatch("getDepositIntent", data)
        .then((resp) => {
          this.loadingPayment = false;
          if (resp && resp.searchPendingPay) {
            this.paymentDetails = resp;
            this.startCountdown(this.paymentDetails.searchPendingPay.createdAt); // Inicia a contagem regressiva
            this.generateQRCode(); // Gera o QR Code
          } else {
            console.error('Dados de pagamento incompletos:', resp);
          }
        })
        .catch((err) => {
          this.loadingPayment = false;
          // console.error('Erro ao buscar detalhes do pagamento:', err);
          this.paymentDetails = {}; // Certifique-se de limpar dados incorretos
        });
    },
    startCountdown(targetDate) {
      // Converte a data de entrada para um objeto Date e adiciona 1 hora
      let target = new Date(targetDate);
      target.setHours(target.getHours() + 1);

      // Função que atualiza a contagem regressiva
      const updateCountdown = () => {
        const now = new Date().getTime();
        const timeDifference = target.getTime() - now;

        if (timeDifference > 0) {
          const hours = Math.floor(
            (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
          );
          const minutes = Math.floor(
            (timeDifference % (1000 * 60 * 60)) / (1000 * 60)
          );
          const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

          this.countdown = `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
        } else {
          this.countdown = "00:00:00";
          clearInterval(countdownInterval);
        }
      };

      // Atualiza a cada segundo
      const countdownInterval = setInterval(updateCountdown, 1000);
      updateCountdown();
    },
    generateQRCode() {
      const depositAddress = this.paymentDetails.depositAddress;
      const value = this.paymentDetails.searchPendingPay.value;

      // USDT BEP-20 contract address on BSC
      const usdtContractAddress = '0x55d398326f99059ff775485246999027b3197955';

      // Correct URI for USDT transfer on Binance Smart Chain
      const uri = `ethereum:${depositAddress}`;

      // Generate the QR code using the qrcode library
      QRCode.toDataURL(uri)
        .then((url) => {
          this.qrCodeData = url; // Save the QR code URL for display
        })
        .catch((err) => {
          // console.error(err);
        });
    },
    allocateValueInTrading(id) {
      this.btnLoading = true;
      const data = {
        robotId: id,
        value: this.allocationValue,
      };
      this.$store
        .dispatch("allocateValueInTrading", data)
        .then(() => {
          this.btnLoading = false;
          this.getDepositIntent(id);
          setTimeout(() => {
            this.$bvModal.hide(`modal-allocateValue-${id}`);
            this.btnLoading = false;
          }, 5000);
        })
        .catch((error) => {
          this.btnLoading = false;
          this.showAlert = true;
          this.colorAlert = "danger";
          this.textAlert = error.message;
        });
    },
    moneyFormat(value) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "USD",
      }).format(value);
    },
  },
};
</script>

<style scoped>
.individually-trading-card {
  margin-top: 2rem;
  padding: 0.5rem;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
}

.card-header {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
}

.card-title h4 {
  font-weight: bold;
}

.operation-time {
  text-align: right;
}

.operation-time span {
  font-size: 0.875rem;
}

.action-buttons .btn {
  margin-bottom: 0.5rem;
}

.withdraw-buttons .btn {
  margin-bottom: 0.5rem;
}

.text-warning {
  color: #ffc107 !important;
}

.mt-1 {
  margin-top: 0.25rem !important;
}

.mt-2 {
  margin-top: 0.5rem !important;
}

.mt-3 {
  margin-top: 1rem !important;
}

.mb-0 {
  margin-bottom: 0 !important;
}

.mb-2 {
  margin-bottom: 0.5rem !important;
}

.font-weight-bold {
  font-weight: bold;
}

.font-weight-bolder {
  font-weight: bolder;
}

.p-2 {
  padding: 0.5rem !important;
}

@media (max-width: 767.98px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .operation-time {
    text-align: left;
    margin-top: 0.5rem;
  }

  .action-buttons .btn {
    font-size: 0.9rem;
  }

  .withdraw-buttons .btn {
    font-size: 0.9rem;
  }

  .d-md-flex {
    display: block !important;
  }

  .mr-md-1,
  .ml-md-1 {
    margin-right: 0 !important;
    margin-left: 0 !important;
  }

  .pr-md-1,
  .pl-md-1 {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
}
</style>
